import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { IonicModule } from '@ionic/angular/lazy';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseComponent } from '@app/core/base/base-component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  standalone: true,
  imports: [IonicModule, ReactiveFormsModule],
})
export class FormInputComponent extends BaseComponent implements OnChanges {
  @Input() label: string = '';
  @Input() placeholder?: string;
  @Input() maxlength?: number;
  @Input() type: 'text' | 'email' | 'password' | 'number' = 'text';
  @Input() controlName: string = '';
  @Input() required: boolean = false;

  @Input() form!: FormGroup;
  @Input() errors: { [key: string]: string } = {};
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() readonly: boolean = false;

  @Output() clicked = new EventEmitter<void>();

  private readonly defaultControl = new FormControl();

  /** フォームコントロール本体 */
  get control(): FormControl {
    if (!this.form || !this.controlName) return this.defaultControl;
    return (this.form.get(this.controlName) as FormControl) ?? this.defaultControl;
  }

  /** バリデーション状態（touched または サーバーエラー時も表示） */
  get isInvalid(): boolean {
    const c = this.control;
    if (!c?.invalid) return false;
    return !!(c.touched || c.errors?.['serverError']);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] || changes['form'] || changes['controlName']) {
      this.syncRequiredValidator();
    }
  }

  /**
   * required 入力に応じて Validators.required を付与/除去する
   */
  private syncRequiredValidator(): void {
    const control = this.control;
    if (!control) return;

    if (this.required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
    control.updateValueAndValidity({emitEvent: false});
  }

  /** エラーメッセージ配列（サーバーエラーメッセージを優先表示） */
  get errorMessages(): string[] {
    const messages: string[] = [];
    const errors = this.control?.errors;

    if (errors) {
      if (typeof errors['serverErrorMessage'] === 'string') {
        messages.push(errors['serverErrorMessage']);
      }
      if (this.errors) {
        for (const key in errors) {
          if (key !== 'serverErrorMessage' && this.errors[key]) messages.push(this.errors[key]);
        }
      }
    }
    return messages;
  }

  /** サイズからフォントサイズを決定 */
  get fontSizePx(): number {
    return this.size === 'sm' ? 14 : this.size === 'lg' ? 18 : 16;
  }

  /** クリアボタン処理 */
  onClear(ev: MouseEvent): void {
    ev.stopPropagation();
    if (this.readonly) return;

    try {
      this.control.setValue(null);
      this.control.markAsUntouched();
      this.control.updateValueAndValidity();
    } catch (error) {
      console.warn('Failed to clear input', error);
    }
  }
}
